// Точка входа веб-табло AntScore (раздел WEB, дизайн v6).
//
// Режимы (по query-параметрам, view-only):
//   ?entry / ?code (без кода)  → WEB2 экран входа по коду/ссылке
//   ?overlay                   → WEB3 компактный оверлей для стрима
//   (по умолчанию)             → WEB1/WEB4 живое табло (адаптив по ширине)
//                                 при finished → WEB5 WebResult
// Источник данных и LIVE/DEMO-фолбэк — в api.ts (?share=, ?demo=, ?code=).

import './theme.css';
import { resolveSource } from './api';
import { Scoreboard } from './scoreboard';
import { Overlay } from './overlay';
import { Entry } from './entry';
import { computeState, type Match, type Snapshot } from './engine';
import { announce } from './announcer';
import { Speech } from './speech';

const rootEl = document.getElementById('app');
if (!rootEl) throw new Error('Не найден контейнер #app');
const root: HTMLElement = rootEl;

const params = new URLSearchParams(window.location.search);
const wantEntry = params.has('entry') || (params.has('code') && !(params.get('code') || '').trim());
const wantOverlay = params.has('overlay');

document.body.dataset.mode = wantEntry ? 'entry' : wantOverlay ? 'overlay' : 'board';

if (wantEntry) {
  // WEB2 — вход по коду/ссылке. Источник данных не нужен.
  new Entry(root).mount();
} else {
  bootLiveScreen();
}

function bootLiveScreen(): void {
  const source = resolveSource();

  if (wantOverlay) {
    // WEB3 — оверлей для стрима.
    const overlay = new Overlay(root);
    source.subscribe((match: Match) => overlay.render(match, source.mode));
    window.addEventListener('beforeunload', () => source.stop());
    return;
  }

  // WEB1/WEB4 — живое табло (+ WEB5 при finished).
  const board = new Scoreboard(root, {
    courtName: params.get('court') ?? 'Клубный корт',
    appVersion: 'v1.0.0',
    shareId: source.shareId ?? '',
  });
  board.startTimer();

  // ---------- Озвучка ----------
  const SOUND_KEY = 'antscore:sound';
  const savedSound = localStorage.getItem(SOUND_KEY);
  const soundOn = savedSound === null ? true : savedSound === '1';
  const speech = new Speech(soundOn);

  let prevSnap: Snapshot | null = null;

  source.subscribe((match: Match) => {
    board.render(match, source.mode);
    const next = computeState(match);
    if (prevSnap !== null && next.pointLogLength === prevSnap.pointLogLength + 1) {
      const phrases = announce(prevSnap, next);
      if (phrases.length) speech.speak(phrases.join(' '), { flush: true });
    }
    prevSnap = next;
  });

  // ---------- UI-тумблер звука ----------
  const toggleBtn = document.createElement('button');
  toggleBtn.className = 'sound-toggle';
  toggleBtn.type = 'button';
  toggleBtn.setAttribute('data-sound-toggle', '');
  document.body.appendChild(toggleBtn);

  function syncToggle(): void {
    const on = speech.isEnabled;
    toggleBtn.classList.toggle('sound-toggle--on', on);
    toggleBtn.classList.toggle('sound-toggle--off', !on);
    toggleBtn.setAttribute('aria-pressed', String(on));
    toggleBtn.title = on ? 'Звук включён' : 'Звук выключен';
    toggleBtn.textContent = on ? '🔊 Звук вкл' : '🔇 Звук выкл';
  }
  syncToggle();

  toggleBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const on = speech.toggle();
    localStorage.setItem(SOUND_KEY, on ? '1' : '0');
    syncToggle();
  });

  // Разблокировка аудио после первого жеста (автоплей-политика браузеров).
  let audioUnlocked = false;
  window.addEventListener('pointerdown', () => {
    if (audioUnlocked) return;
    audioUnlocked = true;
    if (speech.isEnabled && speech.available) speech.speak(' ', { flush: true });
  });

  window.addEventListener('beforeunload', () => {
    source.stop();
    board.stopTimer();
    speech.cancel();
  });
}
